import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isSameDay, addMonths, subMonths } from 'date-fns';
import {
  Plane,
  Clock,
  BarChart2,
  ListChecks,
  AlertCircle,
  Database,
  MapPin,
  Calendar,
  Award,
  CalendarDays,
  ArrowRight,
  Users,
  Plus,
  Edit,
  ChevronLeft,
  ChevronRight,
  X
} from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import CurrencyCard from '../components/CurrencyCard';
import Far117LimitsCard from '../components/Far117LimitsCard';
import FlightRoutesMap from '../components/FlightRoutesMap';
import TripsCard from '../components/TripsCard';

// Add custom CSS to ensure map stays behind navigation
const mapStyles = `
  .map-behind-nav .leaflet-container {
    z-index: 1 !important;
  }
  .map-behind-nav .leaflet-control-container {
    z-index: 1 !important;
  }
  .map-behind-nav .leaflet-popup {
    z-index: 2 !important;
  }
  
  /* Mobile calendar optimizations */
  @media (max-width: 640px) {
    .calendar-grid {
      gap: 2px !important;
    }
    
    .calendar-day {
      min-height: 70px !important;
      padding: 4px !important;
    }
    
    .flight-indicator {
      font-size: 10px !important;
      width: 24px !important;
      height: 24px !important;
    }
    
    /* Improve modal scroll behavior on mobile */
    .modal-content {
      -webkit-overflow-scrolling: touch;
    }
    
    /* Prevent zoom on input focus for mobile */
    input, select, textarea {
      font-size: 16px !important;
    }
  }
`;

// Inject the styles
if (typeof document !== 'undefined') {
  const styleElement = document.createElement('style');
  styleElement.textContent = mapStyles;
  document.head.appendChild(styleElement);
}

// Helper function to get auth headers
const getAuthHeaders = (navigate) => {
  const token = localStorage.getItem('token') || localStorage.getItem('authToken');
  
  if (!token) {
    toast.error('Authentication required. Please login again.');
    navigate('/login');
    return null;
  }
  
  return {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  };
};

// Helper function to handle auth errors
const handleAuthError = (error, navigate) => {
  if (error.response?.status === 401) {
    toast.error('Session expired. Please login again.');
    localStorage.removeItem('token');
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    navigate('/login');
    return true;
  }
  return false;
};

// Helper functions for formatting
const formatNumber = (value) => {
  if (!value) return '0.0';
  return parseFloat(value).toFixed(1);
};

const formatDate = (dateStr) => {
  if (!dateStr) return 'N/A';
  try {
    const date = new Date(dateStr);
    return format(date, 'MMM d, yyyy');
  } catch (error) {
    return 'Invalid Date';
  }
};

// FIXED: Updated formatTime to use datetime fields with VARCHAR fallback
const formatTime = (timeStr) => {
  if (!timeStr) return 'N/A';
  
  // Handle datetime string format first (e.g., "2025-11-29T09:00:00")
  try {
    const date = new Date(timeStr);
    if (!isNaN(date.getTime()) && timeStr.includes('T')) {
      return date.toLocaleTimeString('en-US', {
        hour12: false,
        hour: '2-digit', 
        minute: '2-digit'
      });
    }
  } catch (error) {
    // Fall through to other formats
  }
  
  // Handle 4-digit integer time format (e.g., 955 = 09:55, 1307 = 13:07)
  if (typeof timeStr === 'number' || /^\d{3,4}$/.test(timeStr)) {
    const timeNum = parseInt(timeStr);
    const hours = Math.floor(timeNum / 100);
    const minutes = timeNum % 100;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  }
  
  // Handle string time format
  const timeStrPadded = timeStr.toString().padStart(4, '0');
  return `${timeStrPadded.slice(0, 2)}:${timeStrPadded.slice(2)}`;
};

// FIXED: Timezone-neutral date parsing to prevent calendar date shifting
const parseFlightDate = (dateStr) => {
  if (!dateStr) return null;
  
  try {
    // Parse date string manually to avoid timezone conversion
    // Input: "2025-12-03" or "2025-12-03T08:34:00"
    let datePart = dateStr;
    if (datePart.includes('T')) {
      datePart = datePart.split('T')[0]; // "2025-12-03"
    }
    
    const [year, month, day] = datePart.split('-').map(Number);
    
    // Create date in local timezone without UTC conversion
    return new Date(year, month - 1, day); // month is 0-indexed
  } catch (error) {
    console.warn('Failed to parse flight date:', dateStr);
    return null;
  }
};

const isFlightSoon = (dateStr) => {
  if (!dateStr) return false;
  const flightDate = parseFlightDate(dateStr); // FIXED: Use timezone-neutral parsing
  if (!flightDate) return false;
  const now = new Date();
  const timeDiff = flightDate.getTime() - now.getTime();
  const daysDiff = timeDiff / (1000 * 3600 * 24);
  return daysDiff >= 0 && daysDiff <= 7;
};


// Helper function to format decimal hours as hours:minutes
const formatBlockTime = (decimalHours) => {
  if (!decimalHours || decimalHours === 0) return '0:00';
  
  const hours = Math.floor(decimalHours);
  const minutes = Math.round((decimalHours - hours) * 60);
  
  // Handle case where rounding minutes gives us 60
  if (minutes === 60) {
    return `${hours + 1}:00`;
  }
  
  return `${hours}:${minutes.toString().padStart(2, '0')}`;
};

// Flight Calendar Component
const FlightCalendar = ({ flights }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedFlight, setSelectedFlight] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);
  const [selectedDayFlights, setSelectedDayFlights] = useState([]);

  // Mobile swipe gesture state
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  // Minimum swipe distance required (in px)
  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      nextMonth(); // Swipe left to go to next month
    } else if (isRightSwipe) {
      prevMonth(); // Swipe right to go to previous month
    }
  };

  // Get flights for the current month
  const getFlightsForMonth = () => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(currentMonth);
    
    return flights.filter(flight => {
      const flightDate = parseFlightDate(flight.date); // FIXED: Use timezone-neutral parsing
      return flightDate && flightDate >= monthStart && flightDate <= monthEnd;
    });
  };

  // Get flights for a specific day
  const getFlightsForDay = (day) => {
    return flights.filter(flight => {
      const flightDate = parseFlightDate(flight.date); // FIXED: Use timezone-neutral parsing
      return flightDate && isSameDay(flightDate, day);
    });
  };

  // Generate calendar days
  const generateCalendarDays = () => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(currentMonth);
    
    // Start from the beginning of the week that contains the first day of the month
    const calendarStart = new Date(monthStart);
    calendarStart.setDate(calendarStart.getDate() - monthStart.getDay());
    
    // End at the end of the week that contains the last day of the month
    const calendarEnd = new Date(monthEnd);
    calendarEnd.setDate(calendarEnd.getDate() + (6 - monthEnd.getDay()));
    
    return eachDayOfInterval({ start: calendarStart, end: calendarEnd });
  };

  const days = generateCalendarDays();
  const daysInWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const nextMonth = () => {
    setCurrentMonth(addMonths(currentMonth, 1));
  };

  const prevMonth = () => {
    setCurrentMonth(subMonths(currentMonth, 1));
  };

  const goToToday = () => {
    setCurrentMonth(new Date());
  };

  const navigate = useNavigate();


  return (
    <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-6">
      {/* Calendar Header — on narrow phones the controls wrap below the title */}
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4 sm:mb-6">
        <h2 className="text-lg sm:text-xl font-bold text-gray-900 flex items-center">
          <Calendar className="h-5 w-5 sm:h-6 sm:w-6 mr-2 text-sky-600" />
          Calendar
        </h2>
        <div className="flex items-center space-x-2 sm:space-x-4 ml-auto">
          <button
            onClick={goToToday}
            className="text-xs sm:text-sm text-sky-600 hover:text-sky-800 active:text-sky-900 font-medium px-3 py-2.5 sm:px-3 sm:py-1.5 rounded-lg hover:bg-sky-50 active:bg-sky-100 transition-colors touch-manipulation min-h-[44px] sm:min-h-auto"
          >
            Today
          </button>
          <div className="flex items-center space-x-1 sm:space-x-2">
            <button
              onClick={prevMonth}
              className="p-2.5 sm:p-2 hover:bg-gray-100 active:bg-gray-200 rounded-lg transition-colors touch-manipulation min-w-[44px] min-h-[44px] sm:min-w-auto sm:min-h-auto flex items-center justify-center"
              aria-label="Previous month"
            >
              <ChevronLeft className="h-5 w-5 sm:h-5 sm:w-5 text-gray-600" />
            </button>
            <div className="text-base sm:text-lg font-semibold text-gray-900 min-w-[100px] sm:min-w-[140px] text-center px-2">
              <span className="hidden sm:inline">{format(currentMonth, 'MMMM yyyy')}</span>
              <span className="sm:hidden">{format(currentMonth, 'MMM yyyy')}</span>
            </div>
            <button
              onClick={nextMonth}
              className="p-2.5 sm:p-2 hover:bg-gray-100 active:bg-gray-200 rounded-lg transition-colors touch-manipulation min-w-[44px] min-h-[44px] sm:min-w-auto sm:min-h-auto flex items-center justify-center"
              aria-label="Next month"
            >
              <ChevronRight className="h-5 w-5 sm:h-5 sm:w-5 text-gray-600" />
            </button>
          </div>
        </div>
      </div>

      {/* Calendar Grid */}
      <div 
        className="bg-gray-50 rounded-xl p-2 sm:p-4"
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        {/* Day Headers */}
        <div className="grid grid-cols-7 gap-1 sm:gap-2 mb-2 sm:mb-4">
          {daysInWeek.map((day) => (
            <div key={day} className="text-center text-xs sm:text-sm font-semibold text-gray-700 py-2 sm:py-3">
              <span className="hidden sm:inline">{day}</span>
              <span className="sm:hidden">{day.slice(0, 2)}</span>
            </div>
          ))}
        </div>

        {/* Calendar Days */}
        <div className="grid grid-cols-7 gap-1 sm:gap-2">
          {days.map((day) => {
            const dayFlights = getFlightsForDay(day);
            const isCurrentMonth = isSameMonth(day, currentMonth);
            const isToday = isSameDay(day, new Date());

            return (
              <div
                key={day.toISOString()}
                className={`relative aspect-square p-1 sm:p-2 border rounded-lg transition-all ${
                  isCurrentMonth ? 'bg-white' : 'bg-gray-100'
                } ${
                  isToday ? 'ring-2 ring-sky-500 bg-sky-50' : ''
                } ${
                  dayFlights.length > 0 ? 'cursor-pointer hover:shadow-md' : ''
                } ${
                  dayFlights.length === 1 ? 'bg-sky-50 border-sky-200' :
                  dayFlights.length === 2 ? 'bg-sky-100 border-sky-300' :
                  dayFlights.length === 3 ? 'bg-sky-200 border-sky-400' :
                  dayFlights.length >= 4 ? 'bg-sky-300 border-sky-500' :
                  'border-gray-200'
                }`}
                onClick={() => {
                  if (dayFlights.length > 0) {
                    setSelectedDay(day);
                    setSelectedDayFlights(dayFlights);
                  }
                }}
              >
                {/* Day Number */}
                <div className={`text-left text-sm sm:text-base font-semibold ${
                  isCurrentMonth ? 'text-gray-900' : 'text-gray-400'
                } ${
                  isToday ? 'text-sky-700' : ''
                }`}>
                  {format(day, 'd')}
                </div>
                
                {/* Mobile-Responsive Airplane Icon in Center */}
                {dayFlights.length > 0 && (
                  <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-.05">
                    <Plane className={`h-2 w-2 sm:h-5 sm:w-5 md:h-6 md:w-6 ${
                      dayFlights.length >= 3 ? 'text-sky-700' :
                      dayFlights.length === 2 ? 'text-sky-600' :
                      'text-sky-500'
                    }`} />
                  </div>
                )}
                
                {/* Bottom Bar Indicator with Count */}
                {dayFlights.length > 0 && (
                  <div className={`absolute bottom-0 left-0 right-0 h-2 sm:h-3 rounded-b-lg flex items-center justify-center ${
                    dayFlights.length >= 3 ? 'bg-sky-700' :
                    dayFlights.length === 2 ? 'bg-sky-600' :
                    'bg-sky-500'
                  }`}>
                    <span className="text-xs text-white font-bold">
                      {dayFlights.length}
                    </span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Calendar Summary - Mobile Optimized */}
      <div className="mt-4 sm:mt-6">
        {/* Mobile: Vertical Stack */}
        <div className="sm:hidden space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="text-center p-4 bg-white rounded-xl border border-slate-200 shadow-sm">
              <div className="text-2xl font-bold text-sky-600">{getFlightsForMonth().length}</div>
              <div className="text-sm text-slate-500 mt-1">Flights</div>
            </div>
            <div className="text-center p-4 bg-white rounded-xl border border-slate-200 shadow-sm">
              <div className="text-2xl font-bold text-sky-600">
                {formatNumber(getFlightsForMonth().reduce((sum, flight) => sum + parseFloat(flight.total_time || 0), 0))}h
              </div>
              <div className="text-sm text-slate-500 mt-1">Total Hours</div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="text-center p-4 bg-white rounded-xl border border-slate-200 shadow-sm">
              <div className="text-2xl font-bold text-sky-600">
                {formatNumber(getFlightsForMonth().reduce((sum, flight) => sum + parseFloat(flight.pic_time || 0), 0))}h
              </div>
              <div className="text-sm text-slate-500 mt-1">PIC Hours</div>
            </div>
            <div className="text-center p-4 bg-white rounded-xl border border-slate-200 shadow-sm">
              <div className="text-2xl font-bold text-sky-600">
                {formatBlockTime(getFlightsForMonth().reduce((sum, flight) => 
                  sum + parseFloat(flight.scheduled_block || 0), 0))}
              </div>
              <div className="text-sm text-slate-500 mt-1">Scheduled Block</div>
            </div>
          </div>
        </div>

        {/* Desktop: Horizontal Row */}
        <div className="hidden sm:grid sm:grid-cols-4 gap-4">
          <div className="text-center p-3 bg-white rounded-lg border border-slate-200 shadow-sm">
            <div className="text-2xl font-bold text-sky-600">{getFlightsForMonth().length}</div>
            <div className="text-sm text-slate-500">Flights this month</div>
          </div>
          <div className="text-center p-3 bg-white rounded-lg border border-slate-200 shadow-sm">
            <div className="text-2xl font-bold text-sky-600">
              {formatNumber(getFlightsForMonth().reduce((sum, flight) => sum + parseFloat(flight.total_time || 0), 0))}h
            </div>
            <div className="text-sm text-slate-500">Total hours</div>
          </div>
          <div className="text-center p-3 bg-white rounded-lg border border-slate-200 shadow-sm">
            <div className="text-2xl font-bold text-sky-600">
              {formatNumber(getFlightsForMonth().reduce((sum, flight) => sum + parseFloat(flight.pic_time || 0), 0))}h
            </div>
            <div className="text-sm text-slate-500">PIC hours</div>
          </div>
          <div className="text-center p-3 bg-white rounded-lg border border-slate-200 shadow-sm">
            <div className="text-2xl font-bold text-sky-600">
              {formatBlockTime(getFlightsForMonth().reduce((sum, flight) => 
                sum + parseFloat(flight.scheduled_block || 0), 0))}
            </div>
            <div className="text-sm text-slate-500">Scheduled block</div>
          </div>
        </div>
      </div>

      {/* Day Flights Modal */}
      {selectedDay && selectedDayFlights.length > 0 && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
          <div className="bg-white rounded-xl max-w-md w-full mx-2 sm:mx-4 max-h-[95vh] sm:max-h-[90vh] overflow-hidden">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-slate-800 to-sky-800 px-4 sm:px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="min-w-0 flex-1">
                  <h3 className="text-lg sm:text-xl font-bold text-white truncate">
                    {format(selectedDay, 'MMMM d, yyyy')}
                  </h3>
                  <p className="text-sky-100 text-sm">
                    {selectedDayFlights.length} flight{selectedDayFlights.length !== 1 ? 's' : ''}
                  </p>
                </div>
                <button
                  onClick={() => {
                    setSelectedDay(null);
                    setSelectedDayFlights([]);
                  }}
                  className="p-2.5 sm:p-2 hover:bg-white/20 active:bg-white/30 rounded-lg transition-colors text-white touch-manipulation min-w-[44px] min-h-[44px] sm:min-w-auto sm:min-h-auto flex items-center justify-center"
                  aria-label="Close modal"
                >
                  <X className="h-5 w-5 sm:h-5 sm:w-5" />
                </button>
              </div>
            </div>

            {/* Flights List */}
            <div className="overflow-y-auto max-h-[60vh] p-3 sm:p-6">
              <div className="space-y-3 sm:space-y-4">
                {selectedDayFlights
                  .sort((a, b) => {
                    // Sort by scheduled_out time (departure time)
                    const timeA = parseInt(a.scheduled_out || 0);
                    const timeB = parseInt(b.scheduled_out || 0);
                    return timeA - timeB;
                  })
                  .map((flight, index) => (
                  <div 
                    key={flight.id || index}
                    className="border border-gray-200 rounded-lg p-3 sm:p-4 hover:bg-gray-50 active:bg-gray-100 transition-colors cursor-pointer touch-manipulation"
                    onClick={() => {
                      setSelectedFlight(flight);
                      setSelectedDay(null);
                      setSelectedDayFlights([]);
                    }}
                  >
                    <div className="flex items-center justify-between mb-2 sm:mb-3">
                      <div className="flex items-center space-x-2 sm:space-x-3 min-w-0 flex-1">
                        <div className="w-8 h-8 sm:w-10 sm:h-10 bg-sky-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Plane className="h-4 w-4 sm:h-5 sm:w-5 text-sky-600" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="font-semibold text-gray-900 text-sm sm:text-base truncate">
                            {flight.flight_number || 'N/A'}
                          </div>
                          <div className="text-xs sm:text-sm text-gray-500 truncate">
                            {flight.aircraft_id || 'N/A'}
                          </div>
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0 ml-2">
                        <div className="text-sm sm:text-base font-bold text-gray-900">
                          {formatNumber(flight.total_time)}h
                        </div>
                        <div className="text-xs text-gray-500">
                          Total
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center space-x-2 min-w-0 flex-1">
                        <span className="font-medium text-gray-900 text-xs sm:text-sm">{flight.departure_airport}</span>
                        <ArrowRight className="h-3 w-3 sm:h-4 sm:w-4 text-gray-400 flex-shrink-0" />
                        <span className="font-medium text-gray-900 text-xs sm:text-sm">{flight.arrival_airport}</span>
                      </div>
                      <div className="text-gray-600 font-mono text-xs sm:text-sm ml-2 flex-shrink-0">
                        {flight.scheduled_out && formatTime(flight.scheduled_out)}
                      </div>
                    </div>

                    {flight.pic_time > 0 && (
                      <div className="mt-2 text-xs text-gray-600">
                        PIC: {formatNumber(flight.pic_time)}h
                        {flight.night_time > 0 && ` • Night: ${formatNumber(flight.night_time)}h`}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-3 sm:px-6 py-4 bg-gray-50 border-t">
              <button
                onClick={() => {
                  setSelectedDay(null);
                  setSelectedDayFlights([]);
                }}
                className="w-full px-4 py-3 sm:py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-800 active:bg-slate-900 transition-colors font-medium touch-manipulation"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Individual Flight Detail Modal */}
              {selectedFlight && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[95vh] overflow-hidden">
              {/* Header */}
              <div className="relative bg-gradient-to-r from-slate-800 via-slate-700 to-sky-800 px-8 py-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="p-3 bg-white/20 backdrop-blur-sm rounded-xl">
                      <Plane className="h-8 w-8 text-white" />
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold text-white">
                        Flight {selectedFlight.flight_number || 'N/A'}
                      </h2>
                      <div className="flex items-center space-x-4 mt-1">
                        <p className="text-sky-100 font-medium text-sm">
                          {formatDate(selectedFlight.date)}
                        </p>
                        <div className="flex items-center space-x-2 text-sky-100">
                          <span className="font-semibold text-sm">{selectedFlight.departure_airport}</span>
                          <span className="text-xs">→</span>
                          <span className="font-semibold text-sm">{selectedFlight.arrival_airport}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => setSelectedFlight(null)}
                    className="p-2 hover:bg-white/20 rounded-xl transition-colors text-white"
                  >
                    <X className="h-6 w-6" />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="overflow-y-auto max-h-[calc(95vh-200px)]">
                <div className="p-8">
                  {/* Key Metrics Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
                    <div className="bg-gradient-to-br bg-slate-50 border border-slate-200 rounded-xl p-4">
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-sky-100 rounded-lg">
                          <Clock className="h-5 w-5 text-sky-600" />
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-sky-600 uppercase tracking-wide">Total</p>
                          <p className="text-xl font-bold text-slate-900">{formatNumber(selectedFlight.total_time)}h</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="bg-gradient-to-br bg-slate-50 border border-slate-200 rounded-xl p-4">
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-sky-100 rounded-lg">
                          <Award className="h-5 w-5 text-sky-600" />
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-sky-600 uppercase tracking-wide">PIC</p>
                          <p className="text-xl font-bold text-slate-900">{formatNumber(selectedFlight.pic_time)}h</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="bg-gradient-to-br bg-slate-50 border border-slate-200 rounded-xl p-4">
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-sky-100 rounded-lg">
                          <Users className="h-5 w-5 text-sky-600" />
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-sky-600 uppercase tracking-wide">SIC</p>
                          <p className="text-xl font-bold text-slate-900">{formatNumber(selectedFlight.sic_time || 0)}h</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="bg-gradient-to-br bg-slate-50 border border-slate-200 rounded-xl p-4">
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-sky-100 rounded-lg">
                          <Plane className="h-5 w-5 text-sky-600" />
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-sky-600 uppercase tracking-wide">Aircraft</p>
                          <p className="text-lg font-bold text-slate-900">{selectedFlight.aircraft_id || 'N/A'}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Main Content */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">

                    {/* Flight Information */}
                    <div className="bg-gray-50 rounded-xl p-6">
                      <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                        <div className="p-1.5 bg-gray-200 rounded-lg mr-3">
                          <Plane className="h-4 w-4 text-gray-600" />
                        </div>
                        Flight Information
                      </h3>
                      <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Date</label>
                            <p className="text-gray-900 font-semibold mt-1">{formatDate(selectedFlight.date)}</p>
                          </div>
                          <div>
                            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Flight Number</label>
                            <p className="text-gray-900 font-semibold mt-1">{selectedFlight.flight_number || 'N/A'}</p>
                          </div>
                        </div>
                        
                        <div>
                          <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Aircraft</label>
                          <p className="text-gray-900 font-semibold mt-1">{selectedFlight.aircraft_id || 'N/A'}</p>
                          {selectedFlight.aircraft_type && (
                            <p className="text-xs text-gray-600 font-medium">({selectedFlight.aircraft_type})</p>
                          )}
                        </div>
                        
                        <div>
                          <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Route</label>
                          <div className="mt-1 flex items-center space-x-3">
                            <span className="font-semibold text-gray-900">{selectedFlight.departure_airport}</span>
                            <span className="text-gray-400">→</span>
                            <span className="font-semibold text-gray-900">{selectedFlight.arrival_airport}</span>
                          </div>
                        </div>

                        <div>
                          <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Crew</label>
                          <div className="mt-1 flex items-center space-x-3">
                            <span className="text-gray-500">PIC:</span>
                            <span className="font-semibold text-gray-900">{selectedFlight.pic_crew_name || selectedFlight.pic_crew}</span>
                            <span className="text-gray-500">SIC:</span>
                            <span className="font-semibold text-gray-900">{selectedFlight.sic_crew_name || selectedFlight.sic_crew}</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Flight Times */}
                    <div className="bg-slate-50 rounded-xl p-6">
                      <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                        <div className="p-1.5 bg-sky-100 rounded-lg mr-3">
                          <Clock className="h-4 w-4 text-sky-600" />
                        </div>
                        Times & Hours
                      </h3>
                      <div className="space-y-4">
                        {(selectedFlight.scheduled_out || selectedFlight.scheduled_in) && (
                          <div className="bg-white rounded-lg p-4">
                            <h4 className="text-sm font-semibold text-gray-700 mb-3">Scheduled</h4>
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <label className="text-xs font-medium text-gray-500">Out</label>
                                <p className="font-mono font-semibold text-gray-900">{formatTime(selectedFlight.scheduled_out)}</p>
                              </div>
                              <div>
                                <label className="text-xs font-medium text-gray-500">In</label>
                                <p className="font-mono font-semibold text-gray-900">{formatTime(selectedFlight.scheduled_in)}</p>
                              </div>
                            </div>
                          </div>
                        )}
                        
                        <div className="bg-white rounded-lg p-4">
                          <h4 className="text-sm font-semibold text-gray-700 mb-3">Flight Hours</h4>
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="text-xs font-medium text-gray-500">Total</label>
                              <p className="font-semibold text-gray-900">{formatNumber(selectedFlight.total_time)}h</p>
                            </div>
                            <div>
                              <label className="text-xs font-medium text-gray-500">Night</label>
                              <p className="font-semibold text-gray-900">{formatNumber(selectedFlight.night_time || 0)}h</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Remarks */}
                  {selectedFlight.remarks && (
                    <div className="mt-8">
                      <div className="bg-slate-50 rounded-xl p-6">
                        <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                          <div className="p-1.5 bg-sky-100 rounded-lg mr-3">
                            <AlertCircle className="h-4 w-4 text-sky-600" />
                          </div>
                          Remarks
                        </h3>
                        <div className="bg-white rounded-lg p-4">
                          <p className="text-gray-900 leading-relaxed">{selectedFlight.remarks}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Footer */}
              <div className="px-8 py-4 border-t border-gray-200 bg-gray-50">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-500">
                    Flight View
                  </div>
                  <div className="flex items-center space-x-3">
                    <button
                      onClick={() => {
                        if (selectedFlight && selectedFlight.id) {
                          navigate(`/edit-flight/${selectedFlight.id}`);
                        }
                      }}
                      className="px-6 py-2.5 bg-sky-700 text-white rounded-lg hover:bg-sky-800 transition-colors font-medium flex items-center"
                    >
                      <Edit className="h-4 w-4 mr-2" />
                      Edit Flight
                    </button>
                    <button
                      onClick={() => setSelectedFlight(null)}
                      className="px-6 py-2.5 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
    </div>
  );
};

const Dashboard = () => {
  const [recentFlights, setRecentFlights] = useState([]);
  const [upcomingFlights, setUpcomingFlights] = useState([]);
  const [trips, setTrips] = useState([]);
  const [showAllRecent, setShowAllRecent] = useState(false);
  const [showAllUpcoming, setShowAllUpcoming] = useState(false);

  // How many days back/ahead to show by default before the user opts into "show more"
  const FLIGHT_LIST_WINDOW_DAYS = 14;
  const [allFlights, setAllFlights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedFlight, setSelectedFlight] = useState(null);
  const navigate = useNavigate();
  const { user } = useAuth();
  // Stable so the routes map doesn't refetch on every Dashboard render
  const mapAuthHeaders = useCallback(() => getAuthHeaders(navigate), [navigate]);

  // Helper function to safely parse time values for sorting
  const getTimeValue = (flight) => {
    try {
      const time = flight.scheduled_out || flight.actual_out || flight.departure_time;
      if (!time) return 0;
      
      // Handle both number and string formats
      const timeNum = parseInt(time);
      if (isNaN(timeNum)) return 0;
      
      // Convert HHMM format to minutes for proper sorting
      const hours = Math.floor(timeNum / 100);
      const minutes = timeNum % 100;
      
      // Validate time ranges
      if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
        return 0;
      }
      
      return hours * 60 + minutes;
    } catch (error) {
      console.warn('Error parsing time for flight:', flight.id, error);
      return 0;
    }
  };

  // Helper function to safely parse dates
  const getDateValue = (flight) => {
    try {
      const date = parseFlightDate(flight.date); // FIXED: Use timezone-neutral parsing
      return date || new Date(0);
    } catch (error) {
      console.warn('Error parsing date for flight:', flight.id, error);
      return new Date(0);
    }
  };

  // Fetch dashboard data
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        setError(null);

        const authHeaders = getAuthHeaders(navigate);
        if (!authHeaders) return;

        console.log('Fetching dashboard data...');

        // FIXED: Fetch flight data from /api/flights/recent and handle array validation
        const [flightsRes] = await Promise.all([
          axios.get('/api/flights/dashboard', authHeaders)
        ]);

        const { recentFlights = [], upcomingFlights = [], trips = [] } = flightsRes.data;

        console.log('Processing flights:', recentFlights.length, 'recent,', upcomingFlights.length, 'upcoming');

        // Set all flights for calendar (combined recent + upcoming)
        const flights = [...recentFlights, ...upcomingFlights];
        setAllFlights(flights);

        setRecentFlights(recentFlights);
        setUpcomingFlights(upcomingFlights);


        setTrips(trips);

      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        
        if (!handleAuthError(error, navigate)) {
          setError('Failed to load dashboard data. Please try refreshing the page.');
          toast.error('Failed to load dashboard data');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [navigate]);

  // Handle flight click - show details in modal instead of navigation
  const handleFlightClick = (flight) => {
    if (flight) {
      setSelectedFlight(flight);
    }
  };


  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-600"></div>
            <span className="ml-3 text-gray-600">Loading dashboard...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex flex-col items-center justify-center h-64">
            <AlertCircle className="h-12 w-12 text-red-500 mb-4" />
            <h2 className="text-lg font-semibold text-gray-900 mb-2">Error Loading Dashboard</h2>
            <p className="text-gray-600 mb-4">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-sky-600 text-white rounded-lg hover:bg-sky-700 transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Windowed recent flights: only those within the last FLIGHT_LIST_WINDOW_DAYS days,
  // unless the user has chosen to see everything
  const windowStartDate = new Date();
  windowStartDate.setDate(windowStartDate.getDate() - FLIGHT_LIST_WINDOW_DAYS);
  const windowEndDate = new Date();
  windowEndDate.setDate(windowEndDate.getDate() + FLIGHT_LIST_WINDOW_DAYS);

  const recentFlightsInWindow = recentFlights.filter(
    (flight) => getDateValue(flight) >= windowStartDate
  );
  const displayedRecentFlights = showAllRecent ? recentFlights : recentFlightsInWindow;
  const hasMoreRecentFlights = recentFlights.length > recentFlightsInWindow.length;

  const upcomingFlightsInWindow = upcomingFlights.filter(
    (flight) => getDateValue(flight) <= windowEndDate
  );
  const displayedUpcomingFlights = showAllUpcoming ? upcomingFlights : upcomingFlightsInWindow;
  const hasMoreUpcomingFlights = upcomingFlights.length > upcomingFlightsInWindow.length;

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between gap-4">
            <div className="min-w-0">
              <h1 className="text-2xl font-bold text-gray-900">Welcome back {user?.firstName || ''}!</h1>
              <p className="text-slate-500 mt-1">Here's your flight activity overview</p>
            </div>
            <div className="flex items-center space-x-4">
              <Link 
                to="/add-flight"
                className="inline-flex items-center shrink-0 whitespace-nowrap px-4 sm:px-5 py-2.5 text-sm font-semibold rounded-xl text-white bg-sky-600 hover:bg-sky-700 transition-colors shadow-sm"
              >
                <Plus className="h-4 w-4 mr-2" />
                Log Flight
              </Link>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Flight Calendar */}
          <div className="lg:col-span-2 space-y-8">
            {/* Flight Calendar */}
            <FlightCalendar flights={allFlights} />

            {/* Trips: in progress, upcoming and recently flown */}
            <TripsCard trips={trips} onFlightClick={handleFlightClick} />

            {/* Flight Routes Map */}
            <FlightRoutesMap getAuthHeaders={mapAuthHeaders} />

            {/* Recent Flights List */}
            {recentFlights.length > 0 && (
              <div className="bg-white rounded-2xl shadow-lg p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-bold text-gray-900 flex items-center">
                    <Clock className="h-6 w-6 mr-2 text-sky-600" />
                    Recent Flights
                  </h2>
                  <Link to="/flights" className="text-sm text-sky-600 hover:text-sky-800 font-medium">
                    View All Flights
                  </Link>
                </div>
                
                <div className="space-y-4">
                  {displayedRecentFlights.length === 0 ? (
                    <div className="text-center py-6 text-gray-500">
                      <p className="text-sm">No flights in the last {FLIGHT_LIST_WINDOW_DAYS} days</p>
                      {hasMoreRecentFlights && (
                        <button
                          onClick={() => setShowAllRecent(true)}
                          className="mt-2 text-sm text-sky-600 hover:text-sky-800 font-medium"
                        >
                          Show older flights
                        </button>
                      )}
                    </div>
                  ) : (
                    displayedRecentFlights.map((flight, index) => (
                    <div 
                      key={flight.id || index} 
                      className="group hover:bg-gray-50 rounded-lg p-4 transition-colors duration-200 border border-transparent hover:border-gray-200 cursor-pointer"
                      onClick={() => handleFlightClick(flight)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleFlightClick(flight);
                        }
                      }}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center space-x-4 min-w-0">
                          <div className="flex-shrink-0">
                            <div className="w-10 h-10 rounded-lg bg-sky-100 flex items-center justify-center">
                              <Plane className="h-5 w-5 text-sky-600" />
                            </div>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-2 mb-1">
                              <span className="text-sm font-semibold text-gray-900">
                                {formatDate(flight.date)}
                              </span>
                              {flight.flight_number && (
                                <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                                  {flight.flight_number}
                                </span>
                              )}
                            </div>
                            <div className="flex flex-wrap items-center text-sm text-gray-600">
                              <MapPin className="h-4 w-4 mr-1 text-gray-400" />
                              <span className="font-medium">{flight.departure_airport}</span>
                              <ArrowRight className="h-4 w-4 mx-2 text-gray-400" />
                              <span className="font-medium">{flight.arrival_airport}</span>
                              {flight.aircraft_id && (
                                <span className="ml-3 text-gray-500">
                                  {flight.aircraft_id}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="text-right shrink-0">
                          <div className="text-lg font-bold text-sky-700">
                            {formatNumber(flight.total_time)}h
                          </div>
                          {flight.pic_time > 0 && (
                            <div className="text-xs text-gray-500">
                              {formatNumber(flight.pic_time)} PIC
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    ))
                  )}
                </div>
                {displayedRecentFlights.length > 0 && hasMoreRecentFlights && (
                  <div className="mt-4 text-center">
                    <button
                      onClick={() => setShowAllRecent(!showAllRecent)}
                      className="text-sm text-sky-600 hover:text-sky-800 font-medium"
                    >
                      {showAllRecent
                        ? 'Show fewer flights'
                        : `Show ${recentFlights.length - recentFlightsInWindow.length} more flight${recentFlights.length - recentFlightsInWindow.length === 1 ? '' : 's'}`}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Right Column - Upcoming Flights and Quick Actions */}
          <div className="space-y-8">
            {/* Currency */}
            <CurrencyCard />

            {/* FAR 117 cumulative limits */}
            <Far117LimitsCard />

            {/* Upcoming Flights */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900 flex items-center">
                  <Calendar className="h-6 w-6 mr-2 text-sky-600" />
                  Upcoming Flights
                </h2>
                <Link to="/flights" className="text-sm text-sky-600 hover:text-sky-800 font-medium">
                  View Schedule
                </Link>
              </div>
              
              {upcomingFlights.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <CalendarDays className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p className="text-lg">No upcoming flights</p>
                  <Link 
                    to="/add-flight" 
                    className="inline-flex items-center mt-4 px-4 py-2 text-sm font-medium rounded-lg text-white bg-sky-600 hover:bg-sky-700 transition-colors"
                  >
                    Schedule a Flight
                  </Link>
                </div>
              ) : displayedUpcomingFlights.length === 0 ? (
                <div className="text-center py-6 text-gray-500">
                  <p className="text-sm">No flights in the next {FLIGHT_LIST_WINDOW_DAYS} days</p>
                  {hasMoreUpcomingFlights && (
                    <button
                      onClick={() => setShowAllUpcoming(true)}
                      className="mt-2 text-sm text-sky-600 hover:text-sky-800 font-medium"
                    >
                      Show flights further out
                    </button>
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  {(() => {
                    try {
                      // Additional sorting at display level for extra safety
                      return displayedUpcomingFlights
                        .slice() // Create a copy to avoid mutating the original array
                        .sort((a, b) => {
                          try {
                            // Sort flights by date first, then by departure time
                            const dateA = getDateValue(a);
                            const dateB = getDateValue(b);
                            
                            // If dates are different, sort by date
                            if (dateA.getTime() !== dateB.getTime()) {
                              return dateA - dateB;
                            }
                            
                            // If dates are the same, sort by departure time
                            return getTimeValue(a) - getTimeValue(b);
                          } catch (error) {
                            console.warn('Error sorting upcoming flights at display level:', error);
                            return 0; // Keep original order if sorting fails
                          }
                        });
                    } catch (error) {
                      console.warn('Error in upcoming flights sorting, showing original order:', error);
                      return displayedUpcomingFlights; // Fallback to original array
                    }
                  })()
                    .map((flight, index) => (
                    <div 
                      key={flight.id || index} 
                      className="group hover:bg-gray-50 rounded-lg p-4 transition-colors duration-200 border border-transparent hover:border-gray-200 cursor-pointer"
                      onClick={() => handleFlightClick(flight)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleFlightClick(flight);
                        }
                      }}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          <div className="flex-shrink-0">
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                              isFlightSoon(flight.date || flight.scheduled_out)
                                ? 'bg-sky-100' : 'bg-sky-100'
                            }`}>
                              <Plane className={`h-5 w-5 ${
                                isFlightSoon(flight.date || flight.scheduled_out)
                                  ? 'text-sky-600' : 'text-sky-600'
                              }`} />
                            </div>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-2 mb-1">
                              <span className="text-sm font-semibold text-gray-900">
                                {flight.flight_number || 'N/A'}
                              </span>
                              <span className={`text-xs px-2 py-1 rounded-full ${
                                isFlightSoon(flight.date || flight.scheduled_out)
                                  ? 'text-sky-600 bg-sky-50' : 'text-slate-500 bg-slate-100'
                              }`}>
                                {formatDate(flight.date || flight.scheduled_out)}
                              </span>
                              {isFlightSoon(flight.date || flight.scheduled_out) && (
                                <span className="text-xs text-sky-600 bg-sky-100 px-2 py-1 rounded-full font-medium">
                                  Soon
                                </span>
                              )}
                            </div>
                            <div className="flex items-center text-sm text-gray-600">
                              <MapPin className="h-4 w-4 mr-1 text-gray-400" />
                              <span className="font-medium">{flight.departure_airport}</span>
                              <ArrowRight className="h-4 w-4 mx-2 text-gray-400" />
                              <span className="font-medium">{flight.arrival_airport}</span>
                              {(flight.scheduled_out || flight.actual_out || flight.departure_time) && (
                                <span className="ml-3 text-xs text-gray-500 font-mono">
                                  {formatTime(flight.scheduled_out || flight.actual_out || flight.departure_time)}
                                </span>
                              )}
                              {flight.aircraft_id && (
                                <span className="ml-2 text-gray-500">
                                  {flight.aircraft_id}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-xs text-gray-500">
                            Dep: {formatTime(flight.scheduled_out || flight.departure_time)}
                          </div>
                          {flight.scheduled_in && (
                            <div className="text-xs text-gray-500">
                              Arr: {formatTime(flight.scheduled_in)}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {upcomingFlights.length > 0 && hasMoreUpcomingFlights && (
                <div className="mt-4 text-center">
                  <button
                    onClick={() => setShowAllUpcoming(!showAllUpcoming)}
                    className="text-sm text-sky-600 hover:text-sky-800 font-medium"
                  >
                    {showAllUpcoming
                      ? 'Show fewer flights'
                      : `Show ${upcomingFlights.length - upcomingFlightsInWindow.length} more flight${upcomingFlights.length - upcomingFlightsInWindow.length === 1 ? '' : 's'}`}
                  </button>
                </div>
              )}
            </div>

            {/* Quick Actions */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-5 flex items-center">
                <BarChart2 className="h-6 w-6 mr-2 text-sky-600" />
                Quick Actions
              </h2>
              
              <div className="grid grid-cols-1 gap-3">
                {[
                  { title: "Log New Flight",    description: "Add your latest flight details",    icon: Plane,      link: "/add-flight", iconBg: "bg-sky-100",   iconColor: "text-sky-600" },
                  { title: "View Flight Log",   description: "Browse all recorded flights",       icon: ListChecks, link: "/flights",    iconBg: "bg-slate-100", iconColor: "text-slate-600" },
                  { title: "Flight Statistics", description: "Analyze your flying patterns",      icon: BarChart2,  link: "/statistics", iconBg: "bg-slate-100", iconColor: "text-slate-600" },
                  { title: "Data Manager",      description: "Import or export flight data",      icon: Database,   link: "/data",       iconBg: "bg-slate-100", iconColor: "text-slate-600" },
                ].map((action, index) => (
                  <Link
                    key={index}
                    to={action.link}
                    className="flex items-center space-x-3 p-3 rounded-xl border border-slate-200 hover:border-sky-200 hover:bg-sky-50 transition-colors duration-150 group"
                  >
                    <div className={`p-2.5 rounded-lg ${action.iconBg} flex-shrink-0`}>
                      <action.icon className={`h-5 w-5 ${action.iconColor}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-semibold text-gray-900">{action.title}</h3>
                      <p className="text-xs text-slate-500">{action.description}</p>
                    </div>
                    <ArrowRight className="h-4 w-4 text-slate-300 group-hover:text-sky-500 transition-colors flex-shrink-0" />
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Global Flight Detail Modal - Read-only version for dashboard */}
        {selectedFlight && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[95vh] overflow-hidden">
              {/* Header */}
              <div className="relative bg-gradient-to-r from-slate-800 via-slate-700 to-sky-800 px-8 py-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="p-3 bg-white/20 backdrop-blur-sm rounded-xl">
                      <Plane className="h-8 w-8 text-white" />
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold text-white">
                        Flight {selectedFlight.flight_number || 'N/A'}
                      </h2>
                      <div className="flex items-center space-x-4 mt-1">
                        <p className="text-sky-100 font-medium text-sm">
                          {formatDate(selectedFlight.date)}
                        </p>
                        <div className="flex items-center space-x-2 text-sky-100">
                          <span className="font-semibold text-sm">{selectedFlight.departure_airport}</span>
                          <span className="text-xs">→</span>
                          <span className="font-semibold text-sm">{selectedFlight.arrival_airport}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => setSelectedFlight(null)}
                    className="p-2 hover:bg-white/20 rounded-xl transition-colors text-white"
                  >
                    <X className="h-6 w-6" />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="overflow-y-auto max-h-[calc(95vh-200px)]">
                <div className="p-8">
                  {/* Key Metrics Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
                    <div className="bg-gradient-to-br bg-slate-50 border border-slate-200 rounded-xl p-4">
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-sky-100 rounded-lg">
                          <Clock className="h-5 w-5 text-sky-600" />
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-sky-600 uppercase tracking-wide">Total</p>
                          <p className="text-xl font-bold text-slate-900">{formatNumber(selectedFlight.total_time)}h</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="bg-gradient-to-br bg-slate-50 border border-slate-200 rounded-xl p-4">
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-sky-100 rounded-lg">
                          <Award className="h-5 w-5 text-sky-600" />
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-sky-600 uppercase tracking-wide">PIC</p>
                          <p className="text-xl font-bold text-slate-900">{formatNumber(selectedFlight.pic_time)}h</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="bg-gradient-to-br bg-slate-50 border border-slate-200 rounded-xl p-4">
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-sky-100 rounded-lg">
                          <Users className="h-5 w-5 text-sky-600" />
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-sky-600 uppercase tracking-wide">SIC</p>
                          <p className="text-xl font-bold text-slate-900">{formatNumber(selectedFlight.sic_time || 0)}h</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="bg-gradient-to-br bg-slate-50 border border-slate-200 rounded-xl p-4">
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-sky-100 rounded-lg">
                          <Plane className="h-5 w-5 text-sky-600" />
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-sky-600 uppercase tracking-wide">Aircraft</p>
                          <p className="text-lg font-bold text-slate-900">{selectedFlight.aircraft_id || 'N/A'}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Main Content */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    
                    {/* Flight Information */}
                    <div className="bg-gray-50 rounded-xl p-6">
                      <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                        <div className="p-1.5 bg-gray-200 rounded-lg mr-3">
                          <Plane className="h-4 w-4 text-gray-600" />
                        </div>
                        Flight Information
                      </h3>
                      <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Date</label>
                            <p className="text-gray-900 font-semibold mt-1">{formatDate(selectedFlight.date)}</p>
                          </div>
                          <div>
                            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Flight Number</label>
                            <p className="text-gray-900 font-semibold mt-1">{selectedFlight.flight_number || 'N/A'}</p>
                          </div>
                        </div>
                        
                        <div>
                          <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Aircraft</label>
                          <p className="text-gray-900 font-semibold mt-1">{selectedFlight.aircraft_id || 'N/A'}</p>
                          {selectedFlight.aircraft_type && (
                            <p className="text-xs text-gray-600 font-medium">({selectedFlight.aircraft_type})</p>
                          )}
                        </div>
                        
                        <div>
                          <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Route</label>
                          <div className="mt-1 flex items-center space-x-3">
                            <span className="font-semibold text-gray-900">{selectedFlight.departure_airport}</span>
                            <span className="text-gray-400">→</span>
                            <span className="font-semibold text-gray-900">{selectedFlight.arrival_airport}</span>
                          </div>
                        </div>

                        <div>
                          <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Crew</label>
                          <div className="mt-1 flex items-center space-x-3">
                            <span className="text-gray-500">PIC:</span>
                            <span className="font-semibold text-gray-900">{selectedFlight.pic_crew_name || selectedFlight.pic_crew}</span>
                            <span className="text-gray-500">SIC:</span>
                            <span className="font-semibold text-gray-900">{selectedFlight.sic_crew_name || selectedFlight.sic_crew}</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Flight Times */}
                    <div className="bg-slate-50 rounded-xl p-6">
                      <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                        <div className="p-1.5 bg-sky-100 rounded-lg mr-3">
                          <Clock className="h-4 w-4 text-sky-600" />
                        </div>
                        Times & Hours
                      </h3>
                      <div className="space-y-4">
                        {(selectedFlight.scheduled_out || selectedFlight.scheduled_in) && (
                          <div className="bg-white rounded-lg p-4">
                            <h4 className="text-sm font-semibold text-gray-700 mb-3">Scheduled</h4>
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <label className="text-xs font-medium text-gray-500">Out</label>
                                <p className="font-mono font-semibold text-gray-900">{formatTime(selectedFlight.scheduled_out)}</p>
                              </div>
                              <div>
                                <label className="text-xs font-medium text-gray-500">In</label>
                                <p className="font-mono font-semibold text-gray-900">{formatTime(selectedFlight.scheduled_in)}</p>
                              </div>
                            </div>
                          </div>
                        )}
                        
                        <div className="bg-white rounded-lg p-4">
                          <h4 className="text-sm font-semibold text-gray-700 mb-3">Flight Hours</h4>
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="text-xs font-medium text-gray-500">Total</label>
                              <p className="font-semibold text-gray-900">{formatNumber(selectedFlight.total_time)}h</p>
                            </div>
                            <div>
                              <label className="text-xs font-medium text-gray-500">Night</label>
                              <p className="font-semibold text-gray-900">{formatNumber(selectedFlight.night_time || 0)}h</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Remarks */}
                  {selectedFlight.remarks && (
                    <div className="mt-8">
                      <div className="bg-slate-50 rounded-xl p-6">
                        <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                          <div className="p-1.5 bg-sky-100 rounded-lg mr-3">
                            <AlertCircle className="h-4 w-4 text-sky-600" />
                          </div>
                          Remarks
                        </h3>
                        <div className="bg-white rounded-lg p-4">
                          <p className="text-gray-900 leading-relaxed">{selectedFlight.remarks}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Footer */}
              <div className="px-8 py-4 border-t border-gray-200 bg-gray-50">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-500">
                    Flight View
                  </div>
                  <div className="flex items-center space-x-3">
                    <button
                      onClick={() => {
                        if (selectedFlight && selectedFlight.id) {
                          navigate(`/edit-flight/${selectedFlight.id}`);
                        }
                      }}
                      className="px-6 py-2.5 bg-sky-700 text-white rounded-lg hover:bg-sky-800 transition-colors font-medium flex items-center"
                    >
                      <Edit className="h-4 w-4 mr-2" />
                      Edit Flight
                    </button>
                    <button
                      onClick={() => setSelectedFlight(null)}
                      className="px-6 py-2.5 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;